import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { ItemDto, ItemInput, ItemUnit } from '@tiffinify/contracts';
import type { Food } from '@tiffinify/core';
import { Field, LoadError, PageLoader, useConfirm } from '../components/ui';
import { Back, Btn, Card, Empty, Field as PField, G2, Group, Mk, Note, Row, Screen, Seg, Sp, Title, Top } from '../components/p';
import { ApiClientError, del, post, put } from '../lib/api';
import { errorMessage, formatInr } from '../lib/format';
import { useItems } from '../lib/queries';

const UNITS: Array<{ value: ItemUnit; label: string }> = [{ value: 'kg', label: 'By weight' }, { value: 'pack', label: 'Pack' }, { value: 'box', label: 'Gift box' }, { value: 'plate', label: 'Plate' }];
const grams = (g: number) => (g >= 1000 ? `${g / 1000} kg` : `${g} g`);
export const priceText = (it: Pick<ItemDto, 'unit' | 'price' | 'stepG'>) =>
  it.unit === 'kg' ? `${formatInr(it.price)} a kg · in steps of ${grams(it.stepG)}` : `${formatInr(it.price)} a ${it.unit}`;

/** Items for Menus: what the kitchen sells by weight, pack, box or plate. */
export function Items() {
  const q = useItems();
  const items = q.data?.items;
  return (
    <Screen gap={16}>
      <Top><Back to="/settings/selling" label="Plans, Menus and items" /></Top>
      <Title h="Items for Menus" sub="What your Menus sell. A change shows on new orders; orders already placed keep their price." />
      {q.isError && !items ? <LoadError what="your items" onRetry={() => void q.refetch()} />
        : !items ? <PageLoader label="Finding your items" />
        : items.length === 0 ? <Card><Empty title="No items yet">Add sweets by weight, a thali by the plate or a gift box.</Empty></Card>
        : <Card>{items.map((it) => <Row key={it.id} to={`/items/${it.id}`} left={<Mk food={it.food} />} t={it.name} s={priceText(it)} chev />)}</Card>}
      <Btn label="Add an item" go="plus" to="/items/new" />
    </Screen>
  );
}

/** An item (P20): how it is sold, its price and weight steps, its diet and a note for labels. */
export function ItemPage() {
  const { id = 'new' } = useParams();
  const q = useItems();
  const item = q.data?.items.find((i) => i.id === id);
  if (id !== 'new' && q.isError && !q.data) return <Screen><Top><Back to="/items" label="Items" /></Top><LoadError what="this item" onRetry={() => void q.refetch()} /></Screen>;
  if (id !== 'new' && !q.data) return <Screen><Top><Back to="/items" label="Items" /></Top><PageLoader /></Screen>;
  if (id !== 'new' && !item) return <Screen><Top><Back to="/items" label="Items" /></Top><Card><Empty title="Item not found">It may have been removed.</Empty></Card></Screen>;
  return <ItemForm key={id} item={item} />;
}

function ItemForm({ item }: { item?: ItemDto }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { confirm, sheet } = useConfirm();
  const [name, setName] = useState(item?.name ?? '');
  const [unit, setUnit] = useState<ItemUnit>(item?.unit ?? 'kg');
  const [price, setPrice] = useState(item ? String(item.price) : '');
  const [step, setStep] = useState(String(item?.stepG ?? 250));
  const [min, setMin] = useState(String(item?.minG ?? 250));
  const [food, setFood] = useState<Food>(item?.food ?? 'veg');
  const [note, setNote] = useState(item?.note ?? '');
  const body: ItemInput = { name, unit, price: Number(price) || 0, stepG: Number(step) || 250, minG: Number(min) || 250, food, note };
  const done = () => { void qc.invalidateQueries({ queryKey: ['items'] }); nav('/items', { replace: true }); };
  const save = useMutation({ mutationFn: () => (item ? put<ItemDto>(`/items/${item.id}`, body) : post<ItemDto>('/items', body)), onSuccess: done });
  const remove = useMutation({ mutationFn: () => del(`/items/${item!.id}`), onSuccess: done });
  const fields = save.error instanceof ApiClientError ? save.error.fields : {};

  return (
    <Screen gap={14}>
      <Top>
        <Back to="/items" label="Items" />
        <Sp />
        {item && <button type="button" className="p-link r" disabled={remove.isPending}
          onClick={async () => { if (await confirm({ title: `Remove ${item.name}?`, body: 'It leaves your item list. Orders already placed keep it.', yes: 'Remove', danger: true })) remove.mutate(); }}>Remove</button>}
      </Top>
      <Title h={item ? item.name : 'New item'} sub={item ? 'Sold in your Menus and on orders.' : 'Something your Menus can sell.'} />
      <PField label="Name" id="item-name" value={name} onChange={setName} placeholder="Boondi laddu" error={fields.name} />
      <Seg label="How it is sold" value={unit} onChange={setUnit} options={UNITS} />
      {unit === 'kg' ? (
        <G2>
          <Field label="Price per kg" id="item-price" value={price} onChange={(v) => setPrice(v.replace(/\D/g, ''))} prefix="₹" inputMode="numeric" error={fields.price} />
          <Field label="Sold in steps of (g)" id="item-step" value={step} onChange={(v) => setStep(v.replace(/\D/g, ''))} inputMode="numeric" error={fields.stepG} />
          <Field label="Smallest amount (g)" id="item-min" value={min} onChange={(v) => setMin(v.replace(/\D/g, ''))} inputMode="numeric" error={fields.minG} />
        </G2>
      ) : (
        <Field label={`Price a ${unit}`} id="item-price" value={price} onChange={(v) => setPrice(v.replace(/\D/g, ''))} prefix="₹" inputMode="numeric" error={fields.price} />
      )}
      <Group label="Diet">
        <Seg label="Diet" value={food} onChange={setFood} options={[{ value: 'veg', label: 'Veg' }, { value: 'egg', label: 'Egg' }, { value: 'nonveg', label: 'Non-veg' }]} />
      </Group>
      <PField label="Note (optional, for labels)" id="item-note" value={note} onChange={setNote} placeholder="Gram flour, sugar, ghee · best before 10 days" error={fields.note} />
      {unit === 'kg' && Number(price) > 0 && <Note>{grams(Number(step) || 250)} costs {formatInr(Math.round((Number(price) * (Number(step) || 250)) / 1000))}. Customers can’t order less than {grams(Number(min) || 250)}.</Note>}
      {(save.error || remove.error) && <span className="p-error" role="alert">{errorMessage(save.error ?? remove.error)}</span>}
      <Btn label={item ? 'Save item' : 'Add item'} go="check" busy={save.isPending} disabled={!name.trim() || price === '' || save.isPending} onClick={() => save.mutate()} />
      {sheet}
    </Screen>
  );
}
